import { App, Input, Modal, Tooltip } from 'antd';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import type { CandidateDto } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { FillLines } from '@/components/fill/FillLines';
import { OfficerSearchSheet } from '@/components/fill/OfficerSearchSheet';
import { useFillSheet } from '@/components/fill/useFillSheet';
import { fillAudienceFor, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { useAuth } from '@/hooks/useAuth';
import { namedOfficerIds, slotPosition, type FillCursor } from '@/lib/fillCursor';
import { fillControlsFor } from '@/lib/fillPermissions';
import { closedShortReasonsOf } from '@/lib/shortfallNote';
import { DETAIL_COPY } from '../copy';

/**
 * ─── WHO IS ACTUALLY ON THIS DUTY, ON THE ORDER'S OWN PAGE ───────────────────────
 *
 * [D4.3 §5] The published arrangement showed three numbers and a dot and no people at all;
 * the only path to a name was the per-row "Name officers" link into `/duties/:id`. This is
 * the missing VIEW — not a missing capability, and deliberately not a new one.
 *
 * ⚠️ IT REUSES THE FILL SHEET RATHER THAN ADDING AN ENDPOINT, and that is what makes three
 * of the brief's five requirements free:
 *
 *   · SCOPE (§5e). `GET /duties/:id/fill` applies BOTH filters already — the arrangement's
 *     `visible_org_unit_ids` for the 404, and the share rows' own array for which LINES are
 *     addressed to the caller. A division nodal sees their own lines; the CP office sees all.
 *     A new aggregate read would have had to restate that rule, and a restated authorisation
 *     rule is one that drifts.
 *   · THE SWAP WINDOW (§5d). `swapWindowOpen` is computed server-side from `ends_at` — RESTORED by [C3c §3b] after
 *   [PRE-P6.2] narrowed it to `starts_at + 1h`, so this sentence was false for two passes and is
 *   true again. `namingWindowOpen` is the SECOND, earlier instant, and the two must not be merged, or the
 *     end of the duty date in Asia/Kolkata where no time is set, and published on the DTO.
 *     Nothing here recomputes it — there must be exactly one definition of when a swap is
 *     allowed, and it is `swapWindowClosesAt` in `fulfilment.service.ts`.
 *   · THE SWAP ITSELF (§5c). `useFillSheet` is the one per-slot writer, shared with
 *     `pages/DutyFill`, so this screen writes the same audit row through the same endpoint.
 *
 * ⚠️ CLICK TO OPEN, NEVER HOVER. `SlotChip` already puts its actions behind a `⋮` `Dropdown`
 * with an explicit trigger. Hover-to-reveal was flagged once in this project as inaccessible
 * and dead on touch, and P5's officer surface is touch-first — so the pattern is established
 * here rather than undone there.
 *
 * ⚠️ AND IT DOES NOT STAGE. `pages/DutyFill` batches twelve names behind one Save because
 * that is its whole job; this is a review surface where the operator changes one name, so a
 * pick writes immediately and there is nothing to lose by navigating away.
 */
export const DutyOfficers = ({ dutyId, readOnly }: { dutyId: string; readOnly: boolean }) => {
  const { message } = App.useApp();
  const { officer } = useAuth();
  const { sheet, data, swap, remove } = useFillSheet(dutyId);
  const [cursor, setCursor] = useState<FillCursor | null>(null);
  const [swapReasonFor, setSwapReasonFor] = useState<{ assignmentId: string; officerId: string } | null>(null);
  const [swapReason, setSwapReason] = useState('');

  const lines = data?.lines ?? [];
  /*
   * ⚠️ THE SERVER'S ANSWER, NOT A LOCAL CLOCK COMPARISON. See the header — one definition.
   * `undefined` while loading is treated as CLOSED, so the controls appear when the answer
   * arrives rather than flickering out of existence when it does.
   */
  /*
   * ⚠️ [amended C3c §3d] TWO WINDOWS, AND THIS FILE USED TO COLLAPSE THEM INTO ONE.
   *
   * It read `canWrite = !readOnly && windowOpen` and passed `readOnly={!canWrite}` down — so
   * under [PRE-P6.2]'s narrow swap window it disabled NAMING because SWAPPING had closed. That
   * is exactly the collapse §3c forbids, made permanent. `fillControlsFor` keeps the three
   * inputs apart; this surface performs swaps, so the distinction is visible here first.
   *
   * `undefined` while loading is treated as CLOSED, so the controls appear when the answer
   * arrives rather than flickering out of existence when it does.
   */
  const windowOpen = data?.swapWindowOpen ?? false;
  const controls = fillControlsFor({
    canWrite: !readOnly,
    /*
     * ⚠️ [C3c.2] FALSE HERE, AND NOT BECAUSE OF THE ROLE. `Complete With Short-Fall` lives on
     * the fill SCREEN, which is where the reason is typed and where the resulting sheet is
     * rendered back. This panel is the order's read-across of a duty — it performs swaps and
     * nothing else — so offering the act from it would be a second door onto a write whose
     * confirmation and consequence both live one route over.
     */
    canCompleteShortfall: false,
    namingOpen: data?.namingWindowOpen ?? false,
    swapOpen: windowOpen,
  });

  if (sheet.isPending) return <p className="p-3 text-sm text-muted-foreground">{DETAIL_COPY.officersLoading}</p>;
  if (lines[0] === undefined) return <EmptyState size="sm" title={DETAIL_COPY.officersEmpty} />;

  const openAt = (lineKey: string, allocKey: string, slotRef: string): void => {
    setCursor({ lineKey, allocKey, slotRef, mode: 'swap' });
  };

  const position = cursor ? slotPosition(lines, cursor) : null;

  return (
    <div className="flex flex-col gap-3" data-testid={`duty-officers-${dutyId}`}>
      {!windowOpen && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="swap-window-closed">
          <Lock className="size-3.5" aria-hidden />
          {DETAIL_COPY.windowClosed}
        </p>
      )}

      {/*
        ⚠️ [amended C3c §3d] THE REFUSALS ARE NO LONGER COLLAPSED. `fillControlsFor` answers each
        verb separately, so a duty whose naming window has closed still offers Swap here — which
        is the one thing this surface is for. A role without `arrangement.write` sees a read-only
        app everywhere and needs no sentence; a closed window does, and gets one above.
      */}
      <FillLines
        lines={lines}
        staged={new Map()}
        cursor={cursor}
        controls={controls}
        /*
         * ⚠️ [UX1 §3c] THE SAME DERIVATION THE FILL SCREEN USES, AND NOT A HARD-CODED
         * `'oversight'`.
         *
         * Hard-coding would be defensible on this surface's own header — "WHO IS ACTUALLY ON
         * THIS DUTY" is an oversight question — and it is wrong: a division nodal can open this
         * page (`arrangement.read`), and the same person seeing one post green on the fill
         * screen and amber two clicks away is a defect nobody would be able to explain. §3c
         * says the post reads differently TO TWO ROLES, not on two screens.
         */
        audience={fillAudienceFor(officer?.accessRole ?? 'NONE', actingOrgUnitTypeOf(officer))}
        /* ⚠️ [P7A §4] The fill screen's own derivation — this hidden page shows the same sheet. */
        closedShortReasons={closedShortReasonsOf(data?.shares ?? [])}
        /* An empty slot on THIS surface opens the same search in swap mode's sibling: fill. */
        onAdd={(lineKey, allocKey, slotRef) => {
          setCursor({ lineKey, allocKey, slotRef, mode: 'fill' });
        }}
        onSwap={openAt}
        onRemove={(slotRef, assignmentId) => {
          if (assignmentId) remove.mutate({ slotRef, assignmentId });
        }}
      />

      {data && data.dropped.length > 0 ? (
        <section className="rounded-md border border-border p-3" data-testid="detail-dropped-strip">
          <h3 className="text-sm font-medium">{DETAIL_COPY.droppedHeading}</h3>
          <ul className="mt-1 flex flex-col gap-1 text-sm">
            {data.dropped.map((row) => (
              <li key={row.id}>
                {row.officer?.name ?? '—'} <span className="text-muted-foreground">· {row.lineLabel}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <OfficerSearchSheet
        open={cursor !== null}
        dutyId={dutyId}
        slotRef={cursor?.slotRef ?? ''}
        line={position?.line ?? null}
        slotIndex={position ? position.index + 1 : 0}
        slotTotal={position ? position.total : 0}
        scopeLabel={position?.allocationLabel ?? ''}
        mode={cursor?.mode ?? 'swap'}
        /* ⚠️ [PRE-P6 §2b] The same greying as the fill screen, from the same function. This
           surface stages nothing and closes after every selection, so the sheet alone is the
           whole answer here. */
        namedOfficerIds={namedOfficerIds(lines)}
        onClose={() => {
          setCursor(null);
        }}
        onSelect={(chosen: CandidateDto) => {
          if (!cursor) return;
          const slot = lines
            .flatMap((row) => row.allocations)
            .flatMap((allocation) => allocation.slots)
            .find((row) => row.slotRef === cursor.slotRef);
          const assignment = slot?.assignment ?? null;

          if (assignment) {
            /*
             * ⚠️ THE REASON IS NUDGED ONLY ONCE THE SHARE IS COMPLETE (FR-5.3.7), which is
             * `pages/DutyFill`'s rule and is deliberately not re-decided here. Before that
             * the swap is silent, because a wall in front of every change is how "-" becomes
             * the most common reason in the audit log.
             */
            if (data?.fillState === 'COMPLETE') setSwapReasonFor({ assignmentId: assignment.id, officerId: chosen.id });
            else swap.mutate({ assignmentId: assignment.id, officerId: chosen.id });
          } else {
            void message.error(DETAIL_COPY.slotMoved);
          }
          setCursor(null);
        }}
      />

      <Modal
        open={swapReasonFor !== null}
        title={DETAIL_COPY.swapTitle}
        okText={DETAIL_COPY.swapConfirm}
        data-testid="detail-swap-reason-modal"
        onCancel={() => {
          setSwapReasonFor(null);
          setSwapReason('');
        }}
        onOk={() => {
          if (swapReasonFor) {
            swap.mutate({
              ...swapReasonFor,
              ...(swapReason.trim() ? { reason: swapReason.trim() } : {}),
            });
          }
          setSwapReasonFor(null);
          setSwapReason('');
        }}
      >
        <p className="mb-2 text-sm text-muted-foreground">{DETAIL_COPY.swapIntro}</p>
        <Tooltip title={DETAIL_COPY.swapOptional}>
          <Input.TextArea
            rows={2}
            maxLength={300}
            aria-label={DETAIL_COPY.swapLabel}
            value={swapReason}
            onChange={(event) => {
              setSwapReason(event.target.value);
            }}
          />
        </Tooltip>
      </Modal>
    </div>
  );
};
